// Impersonation API client. Surfaces the admin-only Start/Stop endpoints.
//
// Backend contracts (apps/api/src/domain/Models.Endpoints/Admin/):
//   POST /api/admin/impersonation/start   { id, targetUserId, reason } -> StartImpersonationResponse
//   POST /api/admin/impersonation/stop    { id }                       -> 200 (idempotent)
//
// Start/Stop are `DomainCommand`s: `id` is the aggregate root — the ADMIN's own
// user id (the impersonation events are appended onto the admin's User stream
// for audit). The caller passes it from the auth store (`$auth.user.id`), which
// resolves to the admin even mid-impersonation (the JWT subject stays the admin;
// the `act` claim carries the target). Both rely on the HttpOnly `jwt`
// cookie for auth; the start endpoint rewrites that cookie with the 15-minute
// sidecar-`act` token.

import { api } from './client';
import type {
  ImpersonationDeniedAction,
  UserId,
  StartImpersonationCommand,
  StartImpersonationResponse,
  StopImpersonationCommand,
} from '@voicedojo/backend-types';

export { ImpersonationDeniedAction } from '@voicedojo/backend-types';

export type { StartImpersonationResponse };

export async function startImpersonation(
  adminUserId: UserId,
  targetUserId: UserId,
  reason: string,
): Promise<StartImpersonationResponse> {
  const command: StartImpersonationCommand = {
    id: adminUserId,
    targetUserId,
    reason,
  };
  return api.post<StartImpersonationResponse>('/admin/impersonation/start', command);
}

export async function stopImpersonation(adminUserId: UserId): Promise<unknown> {
  const command: StopImpersonationCommand = { id: adminUserId };
  return api.post<unknown>('/admin/impersonation/stop', command);
}

// The subtraction list, reaching the UI. Impersonation is otherwise additive; the
// server refuses these outright (Models.Write.Security.ImpersonationDenials) and
// names them on the whoami sidecar, so a surface can render them as unavailable
// instead of offering an action that will 403. The list is authored once on the
// server — the FE never re-declares which actions are denied.
export function deniedWhileImpersonating(
  impersonation: { deniedActions?: ImpersonationDeniedAction[] } | null | undefined,
  action: ImpersonationDeniedAction,
): boolean {
  return impersonation?.deniedActions?.includes(action) ?? false;
}
